import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import toast, { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import PageHeader from "../-components/PageHeader";
import { useGetConsolePopups } from "hooks/console/useGetConsolePopups";
import Pagination from "components/Pagination";
import ConsoleTable, { DataListProps } from "../-components/ConsoleTable";
import { useDeleteConsolePopup } from "hooks/console/useDeleteConsolePopup";
import { makeIntComma, RouteTypes, toastErrorSyle } from "hooks/utils/util";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import ConfirmDialog from "components/popup/ConfirmDialog";
import Seo from "components/Seo";

const theadStyle = "text-[#222222] text-[16px] text-center";

export default function ConsolePopups({ type }: { type: RouteTypes }) {
    const initialSearchForm = {
        category: type,
        page: 1,
        limit: "10",
    };
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [searchForm, setSearchForm] = useState(initialSearchForm);
    const allCheckRef = useRef<HTMLInputElement | null>(null);
    const { data, refetch, isLoading } = useGetConsolePopups(searchForm);
    const { mutate: deletePopup } = useDeleteConsolePopup();

    // 사이드바 링크 재 클릭시 상태 초기화
    useEffect(() => {
        if (location.state?.reset) {
            // 초기화 로직 수행
            setSearchForm(initialSearchForm);
        }
    }, [location.state]);

    useEffect(() => {
        const page = searchParams.get("page") ?? "1";
        if (page) {
            setSearchForm(prev => ({ ...prev, page: Number(page) }));
        }
    }, [searchParams]);

    const deleteRow = () => {
        deletePopup(
            { category: type, idx: selectedRows.map(Number) },
            {
                onSuccess: () => {
                    toast.success("성공적으로 삭제 되었습니다.");
                    refetch();
                },
                onError: (error: any) => {
                    const message = error.message;
                    toast.error(message, toastErrorSyle);
                },
            },
        );
    };

    const toggleRow = (id: number) => {
        const allIdxs = data?.data_list.map(item => item.idx);

        setSelectedRows(prev => {
            if (prev.includes(id)) {
                // 이미 선택된 경우 제거
                const newRows = prev.filter(rowId => rowId !== id);
                if (newRows.length !== allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            } else {
                // 없으면 추가
                const newRows = [...prev, id];
                if (newRows.length === allIdxs?.length) allCheckRef.current?.click();
                return newRows;
            }
        });
    };

    const allCheck = (checked: boolean) => {
        const allIdxs = data?.data_list.map(item => item.idx);

        if (checked) {
            if (allIdxs) setSelectedRows(allIdxs);
        } else {
            setSelectedRows([]);
        }
    };

    return (
        <>
            <Seo type="console" pageTitle="팝업관리" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="사이트관리" title="팝업관리" />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className=" px-[40px] py-[28px] w-full overflow-auto">
                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[900px]">
                            <form>
                                <div className="flex justify-end items-center">
                                    <Select
                                        value={searchForm.limit}
                                        onValueChange={value => setSearchForm(prev => ({ ...prev, limit: value }))}
                                    >
                                        <SelectTrigger
                                            className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[150px] border border-[#E1E2E4] bg-white h-[50px]`}
                                        >
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="20">20개</SelectItem>
                                            <SelectItem value="10">10개</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </form>

                            <div className="mt-[8px] mb-[40px]">
                                <div className="bg-[#DCE1EC] flex justify-between px-[30px] py-[20px] rounded-[5px]">
                                    <div className="flex items-center gap-[30px]">
                                        <label htmlFor="all" className="flex items-center gap-x-2">
                                            <input type="checkbox" id="all" onChange={e => allCheck(e.target.checked)} ref={allCheckRef} />
                                            <span>전체선택</span>
                                        </label>
                                        <label htmlFor="" className="text-[#222222] text-[18px]">
                                            선택한 게시글 총 <span className="text-[#4F46E5]">{selectedRows.length}</span>개
                                        </label>
                                    </div>

                                    <ConfirmDialog
                                        title="팝업 삭제"
                                        trigger={open => (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (selectedRows.length === 0) {
                                                        toast.error("삭제할 게시글을 선택해주세요.", toastErrorSyle);
                                                        return;
                                                    }
                                                    open();
                                                }}
                                                className="bg-[#FF5463] text-white text-[14px] rounded-[5px] px-[12px] py-[7.5px]"
                                            >
                                                삭제
                                            </button>
                                        )}
                                        handleConfirm={deleteRow}
                                    />
                                </div>

                                <div className="overflow-x-auto">
                                    <ConsoleTable
                                        thead={[
                                            {
                                                title: "NO",
                                                width: "8%",
                                                key: "index",
                                                content: (row: DataListProps, rowIndex) => {
                                                    const index = data!.total_count - (searchForm.page - 1) * 10 - rowIndex;

                                                    return (
                                                        <label
                                                            htmlFor={`list-${row.idx}`}
                                                            className="flex items-center justify-center gap-x-1 px-2 flex-wrap"
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                id={`list-${row.idx}`}
                                                                checked={selectedRows.includes(row.idx)}
                                                                onChange={() => toggleRow(row.idx)}
                                                            />
                                                            <p className="break-all text-center">{makeIntComma(index) ?? 0}</p>
                                                        </label>
                                                    );
                                                },
                                            },
                                            {
                                                title: "제목",
                                                width: "17%",
                                                key: "vTitle",
                                                content: (row: DataListProps, rowIndex) => (
                                                    <div className="flex items-center justify-center px-2">
                                                        <p className="truncate">{row.vTitle ?? "-"}</p>
                                                    </div>
                                                ),
                                            },
                                            {
                                                title: "팝업창전체크기/1일여부",
                                                width: "16%",
                                                key: "cTodayYN",
                                                className: twMerge(theadStyle, "text-[#666666]"),
                                                content: (row: DataListProps, rowIndex) => (
                                                    <div className="flex items-center justify-center gap-x-[5px] px-2">
                                                        <span>
                                                            {row.vPopSizeW}X{row.vPopSizeH}
                                                        </span>
                                                        / <span>{row.cTodayYN === "Y" ? "노출" : "중지"}</span>
                                                    </div>
                                                ),
                                            },
                                            {
                                                title: "팝업창위치",
                                                width: "15%",
                                                key: "vPopX",
                                                className: twMerge(theadStyle, "text-[#666666]"),
                                                content: (row: DataListProps, rowIndex) => (
                                                    <p className="flex items-center justify-center px-2">
                                                        {row.vPopX}X{row.vPopY}
                                                    </p>
                                                ),
                                            },
                                            {
                                                title: "사용여부",
                                                width: "12%",
                                                key: "cViewYN",
                                                className: twMerge(theadStyle, "text-[#666666]"),
                                                content: (row: DataListProps, rowIndex) => <p>{row.cViewYN === "Y" ? "노출" : "중지"}</p>,
                                            },
                                            {
                                                title: "팝업/레이어",
                                                width: "12%",
                                                key: "cPvL",
                                                className: twMerge(theadStyle, "text-[#666666]"),
                                                content: (row: DataListProps, rowIndex) => <p>{row.cPvL === "P" ? "팝업" : "레이어"}</p>,
                                            },
                                            {
                                                title: "",
                                                width: "10%",
                                                key: "",
                                                className: twMerge(theadStyle, "text-[#666666]"),
                                                content: (row: DataListProps, rowIndex) => (
                                                    <button
                                                        className="px-3 py-2 bg-black text-white rounded-[5px] text-[14px]"
                                                        onClick={() =>
                                                            navigate(`${row.idx}`, { state: { page: searchParams.get("page") ?? "1" } })
                                                        }
                                                    >
                                                        수정
                                                    </button>
                                                ),
                                            },
                                        ]}
                                        tbody={data?.data_list || []}
                                        className="border-b"
                                        uniqueKeyName="idx"
                                        isLoading={isLoading}
                                    />
                                </div>

                                <div className="flex justify-end mt-5">
                                    <button className="bg-black text-white rounded-[5px] px-4 py-2" onClick={() => navigate("create")}>
                                        팝업등록
                                    </button>
                                </div>
                            </div>

                            <Pagination
                                current={searchForm.page}
                                total={data?.total_count || 1}
                                pageSize={Number(searchForm.limit)}
                                onChange={newPage => {
                                    searchParams.set("page", String(newPage));
                                    setSearchParams(searchParams);
                                    // setSearchForm(prev => ({ ...prev, page: newPage }))
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
